import React, { useRef } from 'react';
import { FileGridContainer, MarqueeBox } from './styles';
import { useMarquee } from './hooks/useMarquee';

interface SelectableGridProps {
  /** Click on empty grid space — clear the selection. */
  onBackgroundClick: () => void;
  /** Marquee moved — replace the selection with the intersected entry ids. */
  onMarqueeSelect: (ids: string[]) => void;
  'data-testid'?: string;
  children: React.ReactNode;
}

/**
 * The card grid with marquee selection and click-to-clear on empty space.
 *
 * Extracted from `FileGrid` so the Shared view's grid selects the same way
 * (#1692) rather than through a second copy of the marquee. A card takes part
 * by carrying `data-entry-id`; `useMarquee` hit-tests those inside this
 * container and nothing else.
 */
const SelectableGrid: React.FC<SelectableGridProps> = ({
  onBackgroundClick,
  onMarqueeSelect,
  'data-testid': testId,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // A completed marquee drag fires a trailing click on the container; suppress it
  // so it doesn't immediately clear the freshly-made selection.
  const suppressClickRef = useRef(false);

  const { marqueeRect, onPointerDown } = useMarquee(containerRef, onMarqueeSelect, () => {
    suppressClickRef.current = true;
  });

  return (
    <>
      <FileGridContainer
        ref={containerRef}
        data-testid={testId}
        onPointerDown={onPointerDown}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          onBackgroundClick();
        }}
      >
        {children}
      </FileGridContainer>
      {marqueeRect && (
        <MarqueeBox
          sx={{
            left: marqueeRect.left,
            top: marqueeRect.top,
            width: marqueeRect.width,
            height: marqueeRect.height,
          }}
        />
      )}
    </>
  );
};

export default SelectableGrid;
